import { useEffect, useState } from "react";

import { kaiApi } from "@/services/kaiApi";
import { KaiRateComparison, parseRateComparisonSpec } from "./KaiRateComparison";
import { KaiBlockLoading } from "./KaiBlockLoading";
import { KaiBlockError } from "./KaiBlockError";

type KaiRateComparisonSpec = NonNullable<
  ReturnType<typeof parseRateComparisonSpec>
>;

/**
 * Renders a `rate_comparison` card referenced by ID. The deterministic backend
 * builder persists the card in `kai_artifacts`; the assistant message only
 * carries `{ "id": "..." }`. This resolves that ID to a payload:
 *   1. from the SSE cache (`cachedArtifact`) during the live turn, or
 *   2. by fetching `GET /conversations/:id/artifacts/:artifact_id` on reload.
 */
export function KaiRateComparisonRef({
  artifactId,
  conversationId,
  cachedArtifact,
}: {
  artifactId: string;
  conversationId?: string | null;
  /** The payload delivered via SSE during the live turn, if available. */
  cachedArtifact?: unknown;
}) {
  const cachedSpec = cachedArtifact
    ? parseRateComparisonSpec(JSON.stringify(cachedArtifact))
    : null;

  const [spec, setSpec] = useState<KaiRateComparisonSpec | null>(cachedSpec);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (spec || !conversationId || !artifactId) return;
    let cancelled = false;
    (async () => {
      try {
        const artifact = await kaiApi.getArtifact(conversationId, artifactId);
        const parsed = parseRateComparisonSpec(JSON.stringify(artifact.payload));
        if (cancelled) return;
        if (parsed) setSpec(parsed);
        else setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spec, conversationId, artifactId]);

  if (spec) return <KaiRateComparison spec={spec} />;
  if (failed) return <KaiBlockError tag="rate_comparison" />;
  return <KaiBlockLoading label="Loading rate comparison…" />;
}
